import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useCustomerService } from 'services/customers';
import { useCustomersData } from 'hooks/useCustomersData';
import { Customer } from 'types/customer';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import Pagination from 'components/Pagination';
import { Plus, Search, Eye, Pencil, Trash2, RotateCcw } from 'lucide-react';
import AddCustomerModal from './AddCustomerModal';
import EditCustomerModal from './EditCustomerModal';
import DeleteCustomerModal from './DeleteCustomerModal';
import ViewCustomerModal from './ViewCustomerModal';
import styles from './Customers.module.scss';

function Customers() {
  const customerService = useCustomerService();
  const [includeVoided, setIncludeVoided] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(
    null,
  );
  const [customerToView, setCustomerToView] = useState<Customer | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Use customers data hook
  const {
    customers,
    loading,
    reloading,
    error,
    reload,
    addCustomer,
    updateCustomer,
  } = useCustomersData({ includeVoided });

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Show error toast if there's an error
  useEffect(() => {
    if (error) {
      showToast(error, 'error');
    }
  }, [error, showToast]);

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return customers.filter((customer) => {
      const matchesSearch =
        customer.name.toLowerCase().includes(searchLower) ||
        (customer.email?.toLowerCase().includes(searchLower) ?? false) ||
        (customer.phone?.toLowerCase().includes(searchLower) ?? false) ||
        (customer.address?.toLowerCase().includes(searchLower) ?? false);
      return matchesSearch;
    });
  }, [customers, searchQuery]);

  const totalFilteredCustomers = filteredCustomers.length;

  const paginatedCustomers = useMemo(() => {
    if (totalFilteredCustomers === 0) {
      return [];
    }
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredCustomers.slice(startIndex, endIndex);
  }, [filteredCustomers, currentPage, pageSize, totalFilteredCustomers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, includeVoided]);

  useEffect(() => {
    const nextTotalPages = Math.max(
      1,
      Math.ceil(totalFilteredCustomers / pageSize),
    );
    setCurrentPage((prevPage) => Math.min(prevPage, nextTotalPages));
  }, [totalFilteredCustomers, pageSize]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleAddSuccess = useCallback(
    (customer: Customer) => {
      addCustomer(customer);
      setIsAddModalOpen(false);
      showToast('Customer created successfully', 'success');
    },
    [addCustomer, showToast],
  );

  const handleEditSuccess = useCallback(
    (customer: Customer) => {
      updateCustomer(customer);
      setCustomerToEdit(null);
      showToast('Customer updated successfully', 'success');
    },
    [updateCustomer, showToast],
  );

  const handleDeleteSuccess = useCallback(
    (customer: Customer) => {
      // When voiding, we update the customer with isVoided = true
      updateCustomer(customer);
      setCustomerToDelete(null);
      showToast('Customer voided successfully', 'success');
    },
    [updateCustomer, showToast],
  );

  const handleRestore = useCallback(
    async (customer: Customer) => {
      setIsDeleting(true);
      try {
        const restoredCustomer = await customerService.restoreCustomer(
          customer.id,
        );
        updateCustomer(restoredCustomer);
        showToast('Customer restored successfully', 'success');
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to restore customer. Please try again.';
        showToast(errorMessage, 'error');
      } finally {
        setIsDeleting(false);
      }
    },
    [customerService, updateCustomer, showToast],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Customers</h1>
        <div className={styles.headerButtons}>
          <RefreshButton onClick={reload} loading={reloading} />
          <Button onClick={() => setIsAddModalOpen(true)}>
            <Plus size={18} style={{ marginRight: '0.5rem' }} />
            New Customer
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchContainer}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            className={styles.searchInput}
          />
        </div>
        <label className={styles.filterLabel}>
          <input
            type="checkbox"
            checked={includeVoided}
            onChange={(e) => {
              setIncludeVoided(e.target.checked);
            }}
            className={styles.filterCheckbox}
          />
          Include Voided
        </label>
      </div>

      {/* Error State */}
      {error && customers.length === 0 && (
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button onClick={reload}>Retry</Button>
        </div>
      )}

      {/* Customers Table */}
      {loading ? (
        <div className={styles.tableContainer}>
          <div className={styles.tableSkeleton}>
            <div className={styles.tableSkeletonHeader}>
              {['Name', 'Email', 'Phone', 'Address', 'Status', 'Actions'].map(
                (label) => (
                  <div key={label} className={styles.tableSkeletonHeaderCell}>
                    <span
                      className={`${styles.skeletonLine} ${styles.skeletonMedium}`}
                    />
                  </div>
                ),
              )}
            </div>
            <div className={styles.tableSkeletonBody}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`customer-skeleton-${index}`}
                  className={styles.skeletonRow}
                >
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonLong}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonMedium}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonLong}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : customers.length > 0 ? (
        <div className={styles.tableContainer}>
          {reloading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}>Loading...</div>
            </div>
          )}
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCustomers.map((customer) => (
                <tr key={customer.id}>
                  <td className={styles.nameCell}>{customer.name}</td>
                  <td className={styles.emailCell}>{customer.email || '-'}</td>
                  <td className={styles.phoneCell}>{customer.phone || '-'}</td>
                  <td className={styles.addressCell}>
                    {customer.address || '-'}
                  </td>
                  <td className={styles.statusCell}>
                    {customer.isVoided ? (
                      <span className={styles.voidedBadge}>Voided</span>
                    ) : (
                      <span className={styles.activeBadge}>Active</span>
                    )}
                  </td>
                  <td className={styles.actionsCell}>
                    <button
                      className={styles.viewButton}
                      onClick={() => {
                        setCustomerToView(customer);
                      }}
                      aria-label={`View customer ${customer.id}`}
                      title="View customer"
                    >
                      <Eye size={16} />
                    </button>
                    {!customer.isVoided && (
                      <button
                        className={styles.editButton}
                        onClick={() => {
                          setCustomerToEdit(customer);
                        }}
                        aria-label={`Edit customer ${customer.id}`}
                        title="Edit customer"
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    {!customer.isVoided && (
                      <button
                        className={styles.voidButton}
                        onClick={() => {
                          setCustomerToDelete(customer);
                        }}
                        aria-label={`Void customer ${customer.id}`}
                        title="Void customer"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    {customer.isVoided && (
                      <button
                        className={styles.restoreButton}
                        onClick={() => {
                          void handleRestore(customer);
                        }}
                        aria-label={`Restore customer ${customer.id}`}
                        title="Restore customer"
                        disabled={isDeleting}
                      >
                        <RotateCcw size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Empty State */}
      {!loading && filteredCustomers.length === 0 && !error && (
        <div className={styles.emptyState}>
          <p>
            {searchQuery || includeVoided
              ? 'No customers found matching your filters.'
              : 'No customers yet. Create your first customer to get started.'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalFilteredCustomers > 0 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalItems={totalFilteredCustomers}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {
            setToast(null);
          }}
        />
      )}

      {/* Modals */}
      <AddCustomerModal
        show={isAddModalOpen}
        onHide={() => setIsAddModalOpen(false)}
        onSuccess={handleAddSuccess}
        onError={(message) => showToast(message, 'error')}
      />

      <EditCustomerModal
        show={!!customerToEdit}
        onHide={() => setCustomerToEdit(null)}
        customer={customerToEdit}
        onSuccess={handleEditSuccess}
        onError={(message) => showToast(message, 'error')}
      />

      <DeleteCustomerModal
        show={!!customerToDelete}
        onHide={() => setCustomerToDelete(null)}
        customer={customerToDelete}
        onSuccess={handleDeleteSuccess}
        onError={(message) => showToast(message, 'error')}
      />

      <ViewCustomerModal
        show={!!customerToView}
        onHide={() => setCustomerToView(null)}
        customer={customerToView}
        onEdit={() => {
          setCustomerToView(null);
          if (customerToView) {
            setCustomerToEdit(customerToView);
          }
        }}
        onDelete={() => {
          setCustomerToView(null);
          if (customerToView) {
            setCustomerToDelete(customerToView);
          }
        }}
        onRestore={() => {
          if (customerToView) {
            void handleRestore(customerToView);
          }
        }}
        onError={(message) => showToast(message, 'error')}
      />
    </div>
  );
}

export default Customers;
